/**
 * Shared icon prop type — every SVG icon in `web/src/components/icons/`
 * accepts these props. Each file is a separate module so consumers
 * can `import { SearchIcon } from './icons/SearchIcon'` for clean
 * tree-shaking (do NOT introduce a barrel export — bundlers can't
 * tree-shake re-exports reliably).
 *
 *   size         — pixel width/height; default 20
 *   className    — merged onto the <svg> root for color/layout overrides
 *   aria-label   — optional accessible label. When omitted, the icon
 *                  defaults to `aria-hidden="true"` (decorative).
 *   aria-hidden  — explicit override for the decorative intent. If
 *                  supplied, it overrides the auto-derived default.
 *
 * The icon stroke is `currentColor` so it inherits the surrounding
 * text color, and strokeWidth/linecap/linejoin are locked for visual
 * consistency (issue #438 visual system).
 */
export interface IconProps {
  size?: number;
  className?: string;
  /**
   * Optional accessible label. When omitted (and `aria-hidden` is not
   * explicitly supplied) the icon is treated as decorative and rendered
   * with `aria-hidden="true"` so screen readers skip the glyph.
   */
  'aria-label'?: string;
  /**
   * Explicit decorative / non-decorative intent. When supplied, this
   * wins over the `aria-label`-derived default. Mirrors the standard
   * React `aria-hidden` HTML attribute type so consumers can pass
   * `<GridIcon aria-hidden="true" />` or `aria-hidden={false}`.
   */
  'aria-hidden'?: boolean | 'true' | 'false';
}

/**
 * The shared SVG attrs every icon renders — ensures stroke-width,
 * line-cap, and accessibility defaults are identical across the
 * library. Consumers can still override `size` and `className`.
 */
export const SHARED_ICON_ATTRS = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  xmlns: 'http://www.w3.org/2000/svg',
};
